// src/hooks/useAuth.ts
import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';

export interface User {
  id: number;
  email: string;
  name: string;
  avatar_url: string;
}

const TOKEN_KEY = 'coderai_token';
const USER_KEY = 'coderai_user';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verify stored token on mount; also restore cached user for instant load
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    // Restore cached user immediately so UI shows fast
    const cached = localStorage.getItem(USER_KEY);
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch {}
    }

    // Then verify with server and update (gets latest avatar_url from DB)
    fetch(`${API_URL}/me.php`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setUser(data.user);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const r = await fetch(`${API_URL}/login.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Login failed');
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const r = await fetch(`${API_URL}/register.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Registration failed');
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  // FIX: update both in-memory state AND localStorage so avatar persists on refresh
  const updateAvatar = useCallback((avatar_url: string) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, avatar_url };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // BUG FIX: wrapped in useCallback to prevent stale closures
  const getToken = useCallback((): string => {
    return localStorage.getItem(TOKEN_KEY) ?? '';
  }, []);

  return { user, loading, login, register, logout, updateAvatar, getToken };
}